"use client";

import { Minus, Plus } from "@/components/icons";
import { cn } from "@/lib/utils";
import { useQuantityStepper } from "./use-quantity-stepper";

interface QuantityStepperProps {
  quantity: number;
  minQuantity: number;
  step?: number;
  onUpdateQuantity: (quantity: number) => void | Promise<void>;
  onPendingChange?: (pending: boolean) => void;
  className?: string;
  size?: "default" | "lg";
}

const STEPPER_SIZES = {
  default: {
    frame: "border border-border",
    input: "h-7 w-16 border-x border-border text-sm",
    button: "h-7 w-7",
    icon: "size-3",
  },
  lg: {
    frame: "gap-1.5",
    input:
      "h-11 w-20 rounded-field border border-input bg-card text-base transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[2px] focus-visible:ring-ring/20",
    button:
      "size-11 rounded-field border border-border bg-card hover:bg-surface-2",
    icon: "size-4",
  },
} as const;

export function QuantityStepper(props: QuantityStepperProps) {
  const { minQuantity, className, size = "default" } = props;
  const sizing = STEPPER_SIZES[size];
  const {
    localQty,
    base,
    decrease,
    increase,
    handleChange,
    handleBlur,
    handleFocus,
  } = useQuantityStepper(props);

  return (
    <div className={cn("flex items-center rounded-md", sizing.frame, className)}>
      <StepButton
        label="Decrease quantity"
        className={sizing.button}
        disabled={base <= minQuantity}
        {...decrease}
      >
        <Minus className={sizing.icon} />
      </StepButton>
      <input
        type="text"
        inputMode="numeric"
        value={localQty}
        aria-label="Quantity"
        className={cn(
          "bg-transparent text-center font-medium tabular-nums outline-none",
          sizing.input,
        )}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
      />
      <StepButton
        label="Increase quantity"
        className={sizing.button}
        {...increase}
      >
        <Plus className={sizing.icon} />
      </StepButton>
    </div>
  );
}

type StepButtonProps = Omit<React.ComponentProps<"button">, "className"> & {
  label: string;
  className: string;
};

function StepButton({ label, className, children, ...handlers }: StepButtonProps) {
  return (
    <button
      type="button"
      aria-label={label}
      className={cn(
        "flex select-none touch-manipulation items-center justify-center text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-40",
        className,
      )}
      {...handlers}
    >
      {children}
    </button>
  );
}
